import { memo } from 'react';
import {
    Bar,
    BarChart,
    BarStack,
    CartesianGrid,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { AXIS_TICK, CHART_RESIZE_DEBOUNCE_MS, HOVER_CURSOR } from '../../../utils/chartFormatting';
import { tooltipLines, type OverTimeChartData, type OverTimeRow } from './overTimeRows';

export const OVER_TIME_CHART_HEIGHT_PX = 200;
const CHART_MARGIN = { top: 4, right: 4, left: -20, bottom: 0 } as const;
const STACK_RADIUS: [number, number, number, number] = [3, 3, 0, 0];
const MAX_BAR_PX = 28;
const MIN_TICK_GAP_PX = 16;
const BAR_SWATCH = 'w-2 h-2 rounded-[2px] shrink-0';
// Keep opacity-60 in step with LINE_OPACITY so the legend and tooltip match the line.
const LINE_SWATCH = 'w-3 h-0.5 rounded-full shrink-0 opacity-60';
const LINE_OPACITY = 0.6;
const LINE_ACTIVE_DOT_RADIUS = 3;

interface OverTimeTooltipProps {
    chart: OverTimeChartData;
    active?: boolean;
    payload?: { payload: OverTimeRow }[];
}

interface TooltipCountProps {
    label: string;
    color: string;
    count: number;
    swatch: string;
}

function TooltipCount({ label, color, count, swatch }: TooltipCountProps) {
    return (
        <div className="flex items-center gap-2">
            <span className={swatch} style={{ background: color }} />
            <span className="text-[11px] text-gray-500 flex-1">{label}</span>
            <span className="text-[11px] font-semibold text-gray-900 tabular-nums">
                {count.toLocaleString()}
            </span>
        </div>
    );
}

function OverTimeTooltip({ chart, active, payload }: OverTimeTooltipProps) {
    const row = active ? payload?.[0]?.payload : undefined;
    if (!row) return null;
    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3.5 py-2.5 min-w-[140px]">
            <p className="text-xs font-bold text-gray-800 mb-1.5">{row.label}</p>
            <div className="flex flex-col gap-1">
                {chart.line && (
                    <TooltipCount {...chart.line} count={row.line ?? 0} swatch={LINE_SWATCH} />
                )}
                {tooltipLines(row, chart).map((line) => (
                    <TooltipCount
                        key={line.key}
                        label={line.label}
                        color={line.color}
                        count={line.count}
                        swatch={BAR_SWATCH}
                    />
                ))}
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-gray-100 mt-1.5 pt-1.5">
                <span className="text-[11px] font-semibold text-gray-600">{chart.totalLabel}</span>
                <span className="text-[11px] font-bold text-gray-900 tabular-nums">
                    {row.total.toLocaleString()}
                </span>
            </div>
        </div>
    );
}

function OverTimeLegend({ segments, line }: Pick<OverTimeChartData, 'segments' | 'line'>) {
    return (
        <div className="flex flex-wrap gap-x-3 gap-y-1">
            {segments.map((segment) => (
                <span
                    key={segment.key}
                    className="flex items-center gap-1.5 text-[11px] text-gray-500"
                >
                    <span
                        className="w-2.5 h-2.5 rounded-[3px] shrink-0"
                        style={{ background: segment.color }}
                    />
                    {segment.label}
                </span>
            ))}
            {line && (
                <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                    <span className={LINE_SWATCH} style={{ background: line.color }} />
                    {line.label}
                </span>
            )}
        </div>
    );
}

// Memoised on the built chart, so a re-render with the same response keeps recharts' bars.
export const OverTimeChart = memo(function OverTimeChart({ chart }: { chart: OverTimeChartData }) {
    return (
        <div className="flex flex-col gap-3">
            <ResponsiveContainer
                width="100%"
                height={OVER_TIME_CHART_HEIGHT_PX}
                debounce={CHART_RESIZE_DEBOUNCE_MS}
                className="overflow-x-clip"
            >
                <BarChart data={chart.rows} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis
                        dataKey="label"
                        tick={AXIS_TICK}
                        axisLine={false}
                        tickLine={false}
                        tickMargin={6}
                        interval="preserveStartEnd"
                        minTickGap={MIN_TICK_GAP_PX}
                    />
                    <YAxis
                        tick={AXIS_TICK}
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                    />
                    <Tooltip
                        cursor={HOVER_CURSOR}
                        content={<OverTimeTooltip chart={chart} />}
                        isAnimationActive={false}
                    />
                    {/* no stackId: recharts names clipPaths after it, so a shared id clips every chart to the first */}
                    <BarStack radius={STACK_RADIUS}>
                        {chart.segments.map((segment, index) => (
                            <Bar
                                key={segment.key}
                                dataKey={`segments.${index}`}
                                name={segment.label}
                                fill={segment.color}
                                maxBarSize={MAX_BAR_PX}
                                isAnimationActive={false}
                            />
                        ))}
                    </BarStack>
                    {chart.line && (
                        <Line
                            type="monotone"
                            dataKey="line"
                            name={chart.line.label}
                            stroke={chart.line.color}
                            strokeOpacity={LINE_OPACITY}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{
                                r: LINE_ACTIVE_DOT_RADIUS,
                                strokeWidth: 0,
                                fill: chart.line.color
                            }}
                            isAnimationActive={false}
                        />
                    )}
                </BarChart>
            </ResponsiveContainer>
            <OverTimeLegend segments={chart.segments} line={chart.line} />
        </div>
    );
});
